import { useMemo } from "react";
import { useParams } from "react-router-dom";
import VideoJS from "src/components/compounds/VideoJS/VideoJS";
import LiveScoreboard from "src/components/compounds/LiveScoreboard/LiveScoreboard";
import { useGetTeamByIDQuery, useGetTeamEventLiveStreamQuery } from "src/state/modules/api";
import PageLoader1 from "src/components/atoms/PageLoader/PageLoader";
import Styles from "./LivePage.module.scss";

// Keep the live status fresh (viewer count / availability) while the tab is open, matching
// the schedule's live polling. skipPollingIfUnfocused avoids polling background tabs.
const LIVE_POLL_MS = 30000;

// The player is isolated in its own memoized component so the 30s status poll (which updates
// the viewer count and re-renders the page) never re-creates the Video.js options object and
// reloads the stream mid-playback.
function LivePlayer({ teamId, eventId, poster }: { teamId: string; eventId: string; poster: string }) {
  const options = useMemo(
    () => ({
      controls: true,
      autoplay: false, // click-to-play
      liveui: true,
      // `fill` makes the player fill its container (the fixed 16:9 .playerWrapper) rather
      // than relying on `fluid`, which collapsed to video.js's tiny default size here.
      fill: true,
      poster,
      sources: [
        {
          src: `${process.env.REACT_APP_API_SERVER_URL}/teams/${teamId}/schedule/${eventId}/video-stream/playlist.m3u8`,
          type: "application/x-mpegURL",
        },
      ],
    }),
    [teamId, eventId, poster]
  );

  return <VideoJS options={options} onReady={() => {}} />;
}

export default function EventLivePage() {
  const { id, eid } = useParams();
  const { data: team } = useGetTeamByIDQuery(id ?? "");
  const { data: liveStream, isLoading: liveLoading } = useGetTeamEventLiveStreamQuery(
    { teamID: id ?? "", eventID: eid ?? "" },
    { pollingInterval: LIVE_POLL_MS, skipPollingIfUnfocused: true }
  );

  if (liveLoading) return <PageLoader1 />;

  if (!liveStream || !liveStream.is_playable) {
    return (
      <div className={Styles.liveContainer}>
        <div className={Styles.notAvailable}>This game isn&apos;t streaming live video right now.</div>
      </div>
    );
  }

  return (
    <div className={Styles.liveContainer}>
      <div className={Styles.liveHeader}>
        <span className={Styles.liveBadge}>● LIVE</span>
        <span className={Styles.viewerCount}>{liveStream.viewer_count} watching</span>
        {liveStream.is_muted && <span className={Styles.mutedNote}>Stream is muted by broadcaster</span>}
      </div>
      <div className={Styles.playerWrapper} data-testid="live-video-wrapper">
        <LivePlayer teamId={id ?? ""} eventId={eid ?? ""} poster={liveStream.thumbnail_url} />
        {team && <LiveScoreboard team={team} eventId={eid ?? ""} />}
      </div>
    </div>
  );
}
